import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Modal, Timeline, Spin, Alert, Empty } from 'antd';
import moment from 'moment';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { withUserType } from '../withUserType';
import useMountEffect from '../../hooks/useMountEffect';
import OWButton from '../ow_button/OWButton';
import { STOPLIGHT_COLORS } from '../../utils/DataFormatterUtils';
import { ContactAvatarWithNameEmail } from '../contact_avatar_with_name_email/ContactAvatarWithNameEmail';
import { ENTITY_ADD_REQUEST_STATUS_DISPLAY_NAMES } from '../../constants/EntityAddRequestStatuses';
import { fetchEntityAddRequestAudits } from '../../thunks/entity_add_requests_thunks';
import { EntityAddRequestAudit } from '../../types/entity_add_requests';

// Human-readable label per audit type. Unknown types fall back to a de-underscored,
// sentence-cased string so future backend audit types render without a code change.
const AUDIT_TYPE_DISPLAY_NAMES = {
	request_created: 'Request created',
	request_updated: 'Request updated',
	request_submitted: 'Submitted for review',
	request_approved: 'Request approved',
	request_rejected: 'Request rejected',
	request_deleted: 'Request deleted',
};

// Timeline dot color per audit type, keyed to the same palette the status pills use.
const AUDIT_TYPE_DOT_COLORS = {
	request_approved: STOPLIGHT_COLORS.pureGreen,
	request_rejected: STOPLIGHT_COLORS.pureRed,
	request_submitted: '#6027b0',
};

const formatAuditType = (auditType) => {
	if (AUDIT_TYPE_DISPLAY_NAMES[auditType]) {
		return AUDIT_TYPE_DISPLAY_NAMES[auditType];
	}
	const spaced = String(auditType || '').replace(/_/g, ' ');
	return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : 'Event';
};

const formatStatus = (status) =>
	status ? ENTITY_ADD_REQUEST_STATUS_DISPLAY_NAMES[status] || status : null;

const formatAuditDate = (dateString) =>
	dateString ? moment(dateString).format('MMM D, YYYY h:mma') : '—';

// Read-only status/audit history for a single entity add request. Fetches the audit trail
// on open and renders it as a chronological timeline (oldest first, per the API contract).
function EntityAddRequestAuditsModal({ visible, record, hideModal, fetchAudits }) {
	const [audits, setAudits] = useState<EntityAddRequestAudit[]>([]);
	const [loading, setLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState(undefined);

	useMountEffect(() => {
		const requestId = nullSafeGet('id', record);
		if (!requestId) {
			setLoading(false);
			return;
		}
		fetchAudits(requestId)
			.then((data) => {
				setAudits(data || []);
				setLoading(false);
			})
			.catch((error) => {
				setErrorMessage(
					typeof error === 'string' ? error : 'Something went wrong loading the request history.'
				);
				setLoading(false);
			});
	});

	const renderBody = () => {
		if (loading) {
			return (
				<div className="flex flex-row justify-center" style={{ padding: '32px 0' }}>
					<Spin />
				</div>
			);
		}
		if (errorMessage) {
			return <Alert message={errorMessage} type="error" />;
		}
		if (audits.length === 0) {
			return <Empty description="No history for this request yet." />;
		}
		return (
			<Timeline>
				{audits.map((audit) => {
					const auditType = nullSafeGet('auditType', audit);
					const fromStatus = formatStatus(nullSafeGet('fromStatus', audit));
					const toStatus = formatStatus(nullSafeGet('toStatus', audit));
					const note = nullSafeGet('note', audit);
					// Show the hydrated actor via UserName (name + hover card of full details); fall back
					// to the raw createdBy email when no contact was hydrated.
					const createdByContact = nullSafeGet('createdByContact', audit);
					return (
						<Timeline.Item
							key={nullSafeGet('id', audit)}
							color={AUDIT_TYPE_DOT_COLORS[auditType] || 'gray'}
						>
							<div className="flex flex-col" style={{ gap: '2px' }}>
								<span className="bodymedium text-character-primary">
									{formatAuditType(auditType)}
								</span>
								{fromStatus && toStatus ? (
									<span className="footnoteregular text-character-secondary">
										{fromStatus} &rarr; {toStatus}
									</span>
								) : null}
								{note ? (
									<span className="footnoteregular italic text-character-secondary">“{note}”</span>
								) : null}
								<div className="mt-1">
									<ContactAvatarWithNameEmail
										contact={createdByContact}
										fallbackEmail={nullSafeGetOrElse('createdBy', audit, '')}
										size="small"
									/>
								</div>
								<span className="footnoteregular text-character-secondary">
									{formatAuditDate(nullSafeGet('createdAt', audit))}
								</span>
							</div>
						</Timeline.Item>
					);
				})}
			</Timeline>
		);
	};

	return (
		<Modal
			visible={visible}
			width={520}
			title={`Request History: ${nullSafeGetOrElse('displayName', record, '')}`}
			onCancel={hideModal}
			closable={true}
			destroyOnClose={true}
			footer={[
				<OWButton key="done" type="primary" size="large" onClick={hideModal}>
					Done
				</OWButton>,
			]}
		>
			{renderBody()}
		</Modal>
	);
}

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchAudits: (id) => dispatch(fetchEntityAddRequestAudits(ownProps.userType)(id)),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(EntityAddRequestAuditsModal)
);
